/**
 * GSTR-1 · 3-way reconciliation preview + Sandbox push + Excel export.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import GstinSelector from "@/components/gst/GstinSelector";
import {
  FileSpreadsheet, Upload, RefreshCw, CheckCircle2, AlertTriangle, Send,
  FileJson,
} from "lucide-react";

const SECTION_COLORS = {
  B2B: "text-emerald-500", B2CL: "text-sky-500", B2CS: "text-indigo-500",
  EXPWP: "text-fuchsia-500", EXPWT: "text-fuchsia-400",
  SEZWP: "text-amber-500", SEZWT: "text-amber-400",
};

export default function Gstr1Preview() {
  const now = new Date();
  const [period, setPeriod] = useState(
    String(now.getMonth() + 1).padStart(2, "0") + now.getFullYear()
  );
  const [gstin, setGstin] = useState(null);
  const [reco, setReco] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pushing, setPushing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { period };
      if (gstin?.gstin) params.gstin = gstin.gstin;
      const r = await api.get(`/gstr1/preview/${period}`, { params });
      setReco(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [gstin]);

  const dlXlsx = () =>
    downloadBlob(`/gstr1/export.xlsx/${period}`, `GSTR1-${period}.xlsx`);

  const dlJson = async () => {
    try {
      const r = await api.get(`/gstr1/payload/${period}`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)],
        { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `GSTR1-${period}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("GSTN payload JSON downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payload build failed");
    }
  };

  const pushToPortal = async () => {
    if (!window.confirm(`Push GSTR-1 for ${period} to Sandbox / GST portal? This is irreversible.`)) return;
    setPushing(true);
    try {
      const r = await api.post(`/gstr1/push/${period}`);
      toast.success("Pushed to Sandbox — check ARN in the response");
      console.log("Sandbox push response:", r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Push failed";
      if (String(detail).toLowerCase().includes("insufficient")) {
        toast.error("Sandbox GST · Filing scope not yet enabled on your account. Email sales@sandbox.co.in to unlock.", { duration: 8000 });
      } else {
        toast.error(detail);
      }
    }
    setPushing(false);
  };

  const inr = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="gstr1-preview-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// GST · GSTR-1</div>
          <h1 className="text-3xl font-bold mt-1 font-heading">GSTR-1 · 3-Way Reconciliation</h1>
          <div className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Every outward sales invoice for the period, reconciled against e-invoice IRN
            and e-way bill records. Fix mismatches, download the GSTN JSON, then push to
            Sandbox — or file manually on the portal and record the ARN.
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <GstinSelector value={gstin} onChange={setGstin} testid="gstr1-gstin" />
          <Input value={period} onChange={(e) => setPeriod(e.target.value)}
            placeholder="MMYYYY e.g. 032026" className="w-40 rounded-sm h-9 font-mono"
            data-testid="gstr1-period" />
          <Button onClick={load} disabled={loading} variant="outline"
            className="rounded-sm gap-1.5" data-testid="gstr1-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Reload
          </Button>
          <Button onClick={dlXlsx} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" data-testid="gstr1-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </Button>
          <Button onClick={dlJson} variant="outline" className="rounded-sm gap-1.5" data-testid="gstr1-json">
            <FileJson className="w-4 h-4" /> GSTN JSON
          </Button>
          <Button onClick={pushToPortal} disabled={pushing}
            className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5"
            data-testid="gstr1-push">
            {pushing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Push to Portal
          </Button>
        </div>
      </div>

      {reco && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {[
              ["Invoices", reco.totals.invoice_count, "text-white/90"],
              ["Taxable", inr(reco.totals.taxable), "text-emerald-500"],
              ["CGST", inr(reco.totals.cgst), "text-white/90"],
              ["SGST", inr(reco.totals.sgst), "text-white/90"],
              ["IGST", inr(reco.totals.igst), "text-white/90"],
              ["With issues", reco.totals.with_issues,
               reco.totals.with_issues > 0 ? "text-amber-500" : "text-emerald-500"],
            ].map(([lbl, val, tone]) => (
              <div key={lbl} className="card-sharp p-3">
                <div className="text-[10px] font-mono uppercase text-muted-foreground tracking-widest">{lbl}</div>
                <div className={`text-xl font-bold mt-1 font-mono tabular-nums ${tone}`}>{val}</div>
              </div>
            ))}
          </div>

          <div className="card-sharp overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">Inv No</th>
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-left">Section</th>
                  <th className="p-2 text-left">Customer</th>
                  <th className="p-2 text-left">POS</th>
                  <th className="p-2 text-right">Taxable</th>
                  <th className="p-2 text-right">GST</th>
                  <th className="p-2 text-left">IRN</th>
                  <th className="p-2 text-left">EWB</th>
                  <th className="p-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {reco.rows.map((r) => (
                  <tr key={r.invoice_id} className="hover:bg-white/5">
                    <td className="p-2 font-mono">{r.invoice_number}</td>
                    <td className="p-2 text-muted-foreground">{r.date}</td>
                    <td className={`p-2 font-mono text-xs ${SECTION_COLORS[r.section] || ""}`}>
                      {r.section}
                    </td>
                    <td className="p-2">{r.customer_name || "-"}
                      <div className="text-[10px] font-mono text-muted-foreground">{r.customer_gstin || "URP"}</div>
                    </td>
                    <td className="p-2 text-xs">{r.place_of_supply}</td>
                    <td className="p-2 text-right font-mono tabular-nums">{inr(r.taxable)}</td>
                    <td className="p-2 text-right font-mono tabular-nums">
                      {inr((r.cgst || 0) + (r.sgst || 0) + (r.igst || 0))}
                    </td>
                    <td className="p-2">
                      {r.irn ? (
                        <span className="text-emerald-500" title={r.irn}>
                          <CheckCircle2 className="w-4 h-4 inline" />
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-[10px] font-mono">—</span>
                      )}
                    </td>
                    <td className="p-2 font-mono text-[10px]">{r.ewb_no || "—"}</td>
                    <td className="p-2">
                      {r.ok ? (
                        <span className="text-emerald-500 flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" /> OK
                        </span>
                      ) : (
                        <span className="text-amber-500 flex items-center gap-1" title={r.issues.join(", ")}>
                          <AlertTriangle className="w-3.5 h-3.5" /> {r.issues.length}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {reco.rows.length === 0 && (
                  <tr><td colSpan={10} className="p-8 text-center text-muted-foreground text-sm">
                    No outward invoices in this period. When you save sales invoices, they'll
                    appear here classified into B2B / B2CL / B2CS / Export / SEZ automatically.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
